import ChevronLeft from '@material-ui/icons/ChevronLeft'
import ChevronRight from '@material-ui/icons/ChevronRight'
import { sortBy } from 'lodash'
import React from 'react'
import styled from 'styled-components'

import { Divider, FlexBox } from '../../../components/Base/Base'
import { Box } from '../../../components/Box/Box'
import { Button } from '../../../components/Button/Button'
import { Text } from '../../../components/Text/Text'
import { isConsole } from '../../../config/config'
import { HandlerOf } from '../../../helpers/typeHelper'
import { Job, JobStatusDetail } from '../../../models/job'
import { bgGray, blueGray, lightGray2, royalBlue } from '../../../theme/colors'
import { JobListItem } from '../../Job/JobListItem'
import { ProfileJobOrderBy } from './ProfileJobs'

interface ProfileJobsListProps {
  jobs: Job[]
  jobsPages?: Job[][]
  hasNextPage?: boolean
  fetchNextPage: () => Promise<unknown>
  hasPreviousPage?: boolean
  fetchPreviousPage: () => Promise<unknown>
  isFetching?: boolean
  orderBy?: ProfileJobOrderBy
  handleJobOpen: HandlerOf<Job>
  jobUrlConstructor?: (job: Job) => string | undefined
  isInsideSidebar?: boolean
  hideNoJobsMessage?: boolean
  useNumberedPagination?: boolean
  totalPages?: number
}

export function ProfileJobsList({
  jobs,
  jobsPages,
  handleJobOpen,
  jobUrlConstructor,
  orderBy,
  hasNextPage,
  hasPreviousPage,
  isFetching,
  fetchNextPage,
  fetchPreviousPage,
  isInsideSidebar,
  hideNoJobsMessage,
  useNumberedPagination,
  totalPages,
}: ProfileJobsListProps) {
  const [currentPageNumber, setCurrentPageNumber] = React.useState(0)
  let filteredJobs = jobsPages ? jobsPages[currentPageNumber] : jobs

  if (orderBy === ProfileJobOrderBy.STATUS) {
    filteredJobs = sortBy([...filteredJobs], (obj) => obj.humanStatusDetail).sort((a, b) => {
      if (a.statusDetail === JobStatusDetail.IN_PROGRESS) return -1
      if (b.statusDetail === JobStatusDetail.IN_PROGRESS) return 1
      return 0
    })
  }

  if (!filteredJobs || !filteredJobs.length) {
    if (hideNoJobsMessage) return null

    return (
      <Box mt={5} mb={5}>
        <Text variant="header2" fontColor={lightGray2} align="center" mb={2}>
          {isConsole() ? 'No jobs' : 'No booked or in-progress jobs'}
        </Text>
        {!isConsole() && (
          <Button variant="secondary" size="large" href="/search" margin="0 auto">
            Find loads
          </Button>
        )}
      </Box>
    )
  }

  return (
    <>
      <FlexBox isColumn isFullWidth>
        {filteredJobs.map((job, index) => (
          <React.Fragment key={job.id}>
            <StyledDivider $isInsideSidebar={isInsideSidebar} />
            <FlexBox justifyContent="space-between" alignItems="stretch">
              <FlexBox flex={1} flexDirection="column" alignItems="stretch" isFullWidth>
                <JobListItem
                  index={index}
                  job={job}
                  jobs={filteredJobs}
                  onOpen={handleJobOpen}
                  jobUrlConstructor={jobUrlConstructor}
                  renderAssignedTo={() => null}
                  removeCardLayout
                  isInsideSidebar={isInsideSidebar}
                />
              </FlexBox>
              {
                /* TODO: this can be converted into an anchor tag */
                isInsideSidebar && (
                  <ArrowContainer onClick={() => handleJobOpen(job)}>
                    <ChevronRight htmlColor={blueGray} />
                  </ArrowContainer>
                )
              }
            </FlexBox>
          </React.Fragment>
        ))}
      </FlexBox>

      {!useNumberedPagination && hasNextPage && (
        <Button
          onClick={() => fetchNextPage()}
          variant="primary"
          size="large"
          margin="50px auto 0"
          isLoading={isFetching}
        >
          Load more
        </Button>
      )}

      {useNumberedPagination && totalPages && totalPages > 1 && (
        <FlexBox justifyContent="flex-end" isFullWidth mt={5}>
          <FlexBox justifyContent="center">
            {(hasPreviousPage || currentPageNumber > 0) && (
              <Button
                variant="text"
                onClick={() => fetchPreviousPage().then(() => setCurrentPageNumber(currentPageNumber - 1))}
                icon={<ChevronLeft htmlColor={royalBlue} />}
              />
            )}
            <Text mt={0.2} variant="paragraph1">
              {currentPageNumber + 1} of {totalPages}
            </Text>
            {(hasNextPage || currentPageNumber !== totalPages - 1) && (
              <Button
                variant="text"
                onClick={() => fetchNextPage().then(() => setCurrentPageNumber(currentPageNumber + 1))}
                icon={<ChevronRight htmlColor={royalBlue} />}
              />
            )}
          </FlexBox>
        </FlexBox>
      )}
    </>
  )
}

const StyledDivider = styled(Divider)<{ $isInsideSidebar?: boolean }>`
  margin: ${(p) => (p.$isInsideSidebar ? '0 -24px' : '16px -24px')};
  border-color: ${bgGray};
  border-width: 2px;
`

const ArrowContainer = styled(FlexBox)`
  width: 100px;
  cursor: pointer;
  padding: 24px 40px 0 0;
  justify-content: flex-end;
`
